import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import uuid from 'react-uuid';
import Loader from '../../components/common/loader';
import Menu from '../../components/common/menu';
import NoDataComponent from '../../components/common/no-data-component';
import Button from '../../components/Elements/button/button';
import DropDown from '../../components/McDropdown';
import { OrganisationContext } from '../../context/organisationContext';
import { getItemFromLocalStorage } from '../../helpers/localstorage';
import { getAccountList } from '../../store/features/groupsSlice';
import {
  addTemplateOrganization,
  deleteTemplateOrganization,
  getTemplateOrganizations,
} from '../../store/features/templatesSlice';
import { addToast } from '../../store/features/toastSlice';

/**
 * Which organisations a template is shared with, mirroring
 * components/form-details/form-details-right/form-organizations: the picker is the paged, searchable
 * account network, and adding an organisation already on the template is a no-op server-side.
 */
const TemplateOrganizations = ({ templateId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { hasPermission } = useContext(OrganisationContext);
  const pageRef = useRef(0);

  const { templateOrganizations } = useSelector(state => state.templates);
  const { accountList } = useSelector(state => state.groups);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [search, setSearch] = useState('');

  const canManage = hasPermission('FORMS_ORGANIZATION_MANAGE');
  const orgId = getItemFromLocalStorage('user')?.organizations?.[0]?.id;

  const fetchOrganizations = () => {
    setLoading(true);
    dispatch(getTemplateOrganizations({ id: templateId }))
      .then(() => setLoading(false))
      .catch(() => {
        setLoading(false);
        dispatch(addToast({ error: true, text: 'Could not load the organisations', id: uuid() }));
      });
  };

  const fetchAccounts = (page, merge) => {
    pageRef.current = page;
    dispatch(getAccountList({ organizationID: orgId, merge, params: { page, search }, url: '' })).catch(() => {});
  };

  useEffect(() => {
    fetchOrganizations();
  }, [templateId]);

  useEffect(() => {
    if (orgId) fetchAccounts(0, false);
  }, [orgId, search]);

  const onAdd = async () => {
    if (!selectedOrganization) {
      dispatch(addToast({ error: true, text: 'Select an organisation first', id: uuid() }));
      return;
    }
    setSaving(true);
    try {
      await dispatch(
        addTemplateOrganization({ id: templateId, body: { organization: { id: selectedOrganization.id } } }),
      );
      setSelectedOrganization(null);
      setShowAdd(false);
      fetchOrganizations();
      dispatch(addToast({ error: false, text: 'Organisation added', id: uuid() }));
    } catch (e) {
      dispatch(addToast({ error: true, text: 'Could not add the organisation', id: uuid() }));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async org => {
    try {
      await dispatch(deleteTemplateOrganization({ id: templateId, orgId: org.id }));
      fetchOrganizations();
      dispatch(addToast({ error: false, text: 'Organisation removed', id: uuid() }));
    } catch (e) {
      dispatch(addToast({ error: true, text: 'Could not remove the organisation', id: uuid() }));
    }
  };

  const organizations = templateOrganizations || [];

  return (
    <div className="template-organizations flex-column flex-1 mt-6 mx-6 overflow-auto">
      <div className="flex items-center justify-between mb-4">
        <label className="medium-text">Organisations</label>
        {canManage && !showAdd && (
          // .primary sets width:100% in button.styled.js, which overrides the width prop - so the
          // button is sized by this wrapper, the same way the form organisations editor does it.
          <div style={{ width: '72px' }}>
            <Button className="primary" size="small" label="Add" borderRadius="8px" onClick={() => setShowAdd(true)} />
          </div>
        )}
      </div>

      {showAdd && (
        <div className="flex items-center border-bottom pb-4 mb-2">
          <div className="flex mr-4" style={{ width: '320px' }}>
            <DropDown
              options={accountList?.content}
              selected={selectedOrganization}
              setSelected={setSelectedOrganization}
              placeholder="Select organisation"
              hasMore={!accountList?.last}
              isPaged
              isSearchable
              search={search}
              setSearch={setSearch}
              fetchData={() => fetchAccounts(pageRef.current + 1, true)}
            />
          </div>
          <div className="flex items-center col-gap-2">
            <div style={{ width: '80px' }}>
              <Button
                className="default-secondary"
                size="small"
                label="Cancel"
                borderRadius="8px"
                onClick={() => {
                  setShowAdd(false);
                  setSelectedOrganization(null);
                }}
              />
            </div>
            <div style={{ width: '80px' }}>
              <Button
                className={saving ? 'primary disabled' : 'primary'}
                size="small"
                label={saving ? 'Saving…' : 'Save'}
                borderRadius="8px"
                onClick={onAdd}
              />
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <Loader height={32} width={32} />
      ) : organizations.length === 0 ? (
        <NoDataComponent />
      ) : (
        <>
          <div className="list-header pxy-4">
            <label className="medium-text">{t('NAME')}</label>
            <label className="medium-text" />
          </div>
          {organizations.map(org => (
            <div key={org.id} className="list-row pxy-4">
              {/* The name comes from organization-service; a failed lookup leaves it null. */}
              <label className="regular-text">{org.name || org.id}</label>
              <div className="flex items-center justify-end">
                {canManage && <Menu menuList={[{ name: t('DELETE'), onClick: () => onDelete(org) }]} />}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default TemplateOrganizations;
